import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { CoreLink, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity, Langcodes } from '@uncinc/react-drupal-core/types';
import {
  useWindowDimensions,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  BLACK,
  WHITE,
  SCREEN_WIDE,
} from '../../../../config/constants';
import { NodeProject } from '../../../../types/uncinc_profile/nodes';
import { SVGInline } from '../../../../components';
import useBubble from '../../../../helpers/useBubble';

import './style.scss';

const DEACTIVATE_EVENT = 'nodeProjectTeaserDeactivate';

interface NodeProjectTeaserProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeProject>;
  classes: string;
}

const NodeProjectTeaser: React.FC<NodeProjectTeaserProps> = ({ entity, classes, ...props }) => {
  const project = entity.toJS();
  const title = project.title[0].value;
  const partner = project.field_partner[0]?.entity;
  const partnerName = partner?.title[0].value;
  const partnerLogoColor = partner?.field_media_item[0];
  const partnerLogoWhite = partner?.field_media_item_secondary[0];

  // If we have no teaser image and the hero media is not an image, at least don't crash the page
  if (!project.field_teaser_image[0] && project.field_media_item[0]?.bundle !== 'image') {
    return (<div className="NodeProjectTeaser" />);
  }

  const language = useSelector<Immutable.Map<any, any>>((state) => state.getIn(['ui', 'language']));
  const path = project.paths[language as Langcodes];

  const primaryColor = project.field_color_primary[0]?.value || WHITE;
  const secondaryColor = project.field_color_secondary[0]?.value || BLACK;

  const { width } = useWindowDimensions();
  const isTablet = width < SCREEN_WIDE;

  const contentRef = useRef<HTMLDivElement>(null);

  const partnerSlug = partnerName?.replace(/ /g, '-');

  const colorOnHover = (hover: boolean) => {
    if (contentRef.current) {
      contentRef.current.style.opacity = (hover || isTablet) ? '1' : '0';
    }
  };

  const {
    BubbleComponent,
    parentRef,
    onEnter,
    onLeave,
    onFocusIn,
    onFocusOut,
    close,
  } = useBubble(
    primaryColor,
    partnerSlug,
  );

  // Use a ref so the event listener always calls the latest close/colorOnHover
  // without needing to re-register on every render.
  const closeCardRef = useRef<() => void>(null!);
  closeCardRef.current = () => {
    close();
    colorOnHover(false);
  };

  useEffect(() => {
    const handler = () => closeCardRef.current();
    document.addEventListener(DEACTIVATE_EVENT, handler);
    return () => document.removeEventListener(DEACTIVATE_EVENT, handler);
  }, []);

  // @ts-ignore
  const enter = (event) => {
    document.dispatchEvent(new CustomEvent(DEACTIVATE_EVENT));
    onEnter(event);
    colorOnHover(true);
  };

  // @ts-ignore
  const leave = (event) => {
    onLeave(event);
    colorOnHover(false);
  };

  const handleFocus = () => {
    document.dispatchEvent(new CustomEvent(DEACTIVATE_EVENT));
    onFocusIn();
    colorOnHover(true);
  };

  const handleBlur = () => {
    onFocusOut();
    colorOnHover(false);
  };

  return (
    <div className={classNames('NodeProjectTeaser', classes)} {...props}>
      <CoreLink
        className="NodeProjectTeaser__link"
        href={path}
        ariaLabel={`${partnerName}, ${title}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <div
          ref={parentRef}
          className="NodeProjectTeaser__card"
          onMouseEnter={enter}
          onMouseLeave={leave}
          style={{
            backgroundColor: `#${primaryColor}`,
          }}
        >
          <div className="NodeProjectTeaser__image">
            <DynamicMediaItem
              media={project.field_teaser_image[0] || project.field_media_item[0]}
              alt=""
              imageAspectRatio="2_3"
              mediaProps={{ role: 'presentation' }}
            />
            {partnerLogoColor && (
              <div className="NodeProjectTeaser__logo" id={`logo-${partnerSlug}`}>
                <SVGInline url={partnerLogoColor.source} className="NodeProjectTeaser__logo__color" />
                {partnerLogoWhite && (
                  <SVGInline url={partnerLogoWhite.source} className="NodeProjectTeaser__logo__white" />
                )}
              </div>
            )}
            <BubbleComponent className={partnerSlug} />
            <div className="NodeProjectTeaser__gradient" />
          </div>
          <div className="NodeProjectTeaser__wrapper">
            <div
              ref={contentRef}
              className="NodeProjectTeaser__content"
            >
              <h3 className="NodeProjectTeaser__title" aria-describedby="title-desc">
                {title}
              </h3>
              {partnerName && (
                <p
                  id="title-desc"
                  className="NodeProjectTeaser__partner"
                  style={{
                    color: `#${secondaryColor}`,
                  }}
                >
                  {partnerName}
                </p>
              )}
              <WysiwygField
                html={project.body[0]?.summary}
                className="NodeProjectTeaser__body"
              />
            </div>
          </div>
        </div>
      </CoreLink>
    </div>
  );
};

export default NodeProjectTeaser;
